import { FormattedMessage } from "react-intl";

import { Divider } from "@ps-design-system/components/Divider";
import {
  RadioGroupField,
  RadioGroupItem,
} from "@ps-design-system/components/RadioGroup";
import { Text } from "@ps-design-system/components/Text";

import { AddressFields } from "../../AddressForm";
import type { SelectOption } from "../../app-main/types/general.types";

import {
  BILLING_ADDRESS_FIELDS,
  BILLING_CHOICE,
} from "./AddSpaceAddressForm.consts";
import type { BillingChoice, Country } from "./AddSpaceAddressForm.types";

type BillingAddressSectionProps = {
  /** Which billing option the host currently has selected. */
  billingChoice: BillingChoice;
  countryDisabled: boolean;
  countryLabels?: Country["labels"];
  countryOptions: SelectOption[];
  zoneOptions: SelectOption[];
};

/**
 * The billing half of the address form: the same-or-different choice, and a second
 * set of address fields once the host picks "a different address".
 *
 * Only rendered for a host who does not already have billing on file.
 */
const BillingAddressSection = ({
  billingChoice,
  countryDisabled,
  countryLabels,
  countryOptions,
  zoneOptions,
}: BillingAddressSectionProps) => (
  <>
    <Text element="h3" typeface="headline-2-m">
      <FormattedMessage
        id="hosting.address_form.billing_address_heading"
        defaultMessage="Billing Address"
      />
    </Text>
    <Divider spacing={16} />
    <Text element="p" typeface="body-2">
      <FormattedMessage
        id="hosting.address_form.billing_address_text"
        defaultMessage="This will be used when processing your payouts."
      />
    </Text>

    <RadioGroupField name="billingChoice">
      <RadioGroupItem
        label={
          <FormattedMessage
            id="hosting.address_form.billing_choice_same"
            defaultMessage="Use the same address"
          />
        }
        value={BILLING_CHOICE.SameAsSpace}
      />
      <RadioGroupItem
        label={
          <FormattedMessage
            id="hosting.address_form.billing_choice_different"
            defaultMessage="Use a different address"
          />
        }
        value={BILLING_CHOICE.DifferentAddress}
      />
    </RadioGroupField>

    {billingChoice === BILLING_CHOICE.DifferentAddress && (
      <AddressFields
        countryDisabled={countryDisabled}
        countryLabels={countryLabels}
        countryOptions={countryOptions}
        names={BILLING_ADDRESS_FIELDS}
        testIdPrefix="billing"
        zoneOptions={zoneOptions}
      />
    )}
  </>
);

export { BillingAddressSection };

export type { BillingAddressSectionProps };
